import React, { useEffect, useRef, useState } from 'react'
import {
  CCol, CRow, CAlert, CForm, CFormInput, CFormLabel, CButton, CSpinner,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CInputGroup, CInputGroupText,
} from '@coreui/react'
import { api } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  { key: 'empresaCodigo', title: 'Codigo' },
  { key: 'empresaNombre', title: 'Nombre' },
  { key: 'empresaRepreLegal', title: 'Repre Legal' },
  { key: 'empresaFechaCreacion', title: 'Fecha Creacion', type: 'date' },
  { key: 'empresaHoraCreacion', title: 'Hora Creacion' },
  { key: 'empresaDireccion', title: 'Direccion' },
  { key: 'empresaTelefono', title: 'Telefono' },
  { key: 'empresaCelular', title: 'Celular' },
  { key: 'empresaEmail', title: 'Email' },
  { key: 'empresaUltimoPago', title: 'Ultimo Pago', type: 'date' },
  { key: 'empresaSaldoActual', title: 'Otros Saldos Pendientes', type: 'currency' },
  { key: 'empresaEstado', title: 'Estado', type: 'number' },
  { key: 'empresaTotalPagos', title: 'Total Pagos', type: 'currency' },
  { key: 'empresaProximoCobro', title: 'Proximo Cobro', type: 'date' },
  { key: 'empresaPais', title: 'Pais' },
  { key: 'empresaCiudad', title: 'Ciudad' },
  { key: 'empresaTipoMoneda', title: 'Tipo Moneda' },
  { key: 'empresaMotivoSaldo', title: 'Motivo Saldo' },
]

// Empresas — réplica de empresageneral.cs:53 (empresageneral GX).
const EmpresasPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')
  const { aviso, setAviso } = usePendiente()
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  // Fila en edición (empresaww.cs:3456 S212 'DO UPDATE' → webobject empresa Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [form, setForm] = useState({
    empresaNombre: '', empresaRepreLegal: '', empresaDireccion: '', empresaTelefono: '',
    empresaCelular: '', empresaEmail: '', empresaPais: '', empresaCiudad: '',
    empresaTipoMoneda: '', empresaMotivoSaldo: '', empresaEstado: '', empresaSaldoActual: '',
  })

  const cargar = () => {
    setCargando(true)
    api.get<any[]>('/superadmin/empresas')
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [])

  const cambiarEstado = async (fila: any, accion: 'activate' | 'deactivate', verbo: string) => {
    setError('')
    try {
      await api.post(`/superadmin/companies/${fila.empresaCodigo}/${accion}`, {})
      setExito(`Empresa ${fila.empresaCodigo} ${verbo} correctamente`)
      cargar()
    } catch (e: any) {
      setError(e.message)
    }
  }

  // Modificar (empresaww.cs:3456-3464 S212 'DO UPDATE'): carga la fila.
  // EmpresaCodigo es llave y no cambia. El backend replica el BC Cuenta UPD
  // (empresa_bc.cs, BC000F8) sobre los campos editables del grid.
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      empresaNombre: fila.empresaNombre ?? '',
      empresaRepreLegal: fila.empresaRepreLegal ?? '',
      empresaDireccion: fila.empresaDireccion ?? '',
      empresaTelefono: fila.empresaTelefono ?? '',
      empresaCelular: fila.empresaCelular ?? '',
      empresaEmail: fila.empresaEmail ?? '',
      empresaPais: fila.empresaPais ?? '',
      empresaCiudad: fila.empresaCiudad ?? '',
      empresaTipoMoneda: fila.empresaTipoMoneda ?? '',
      empresaMotivoSaldo: fila.empresaMotivoSaldo ?? '',
      empresaEstado: String(fila.empresaEstado ?? ''),
      empresaSaldoActual: String(fila.empresaSaldoActual ?? ''),
    })
    setAbierto(true)
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      // UPD: la llave va en la URL (empresaww.cs:3456 S212 → empresa Mode="UPD")
      await api.put(`/superadmin/empresas/${editando.empresaCodigo}`, {
        empresaNombre: form.empresaNombre || undefined,
        empresaRepreLegal: form.empresaRepreLegal || undefined,
        empresaDireccion: form.empresaDireccion || undefined,
        empresaTelefono: form.empresaTelefono || undefined,
        empresaCelular: form.empresaCelular || undefined,
        empresaEmail: form.empresaEmail || undefined,
        empresaPais: form.empresaPais || undefined,
        empresaCiudad: form.empresaCiudad || undefined,
        empresaTipoMoneda: form.empresaTipoMoneda || undefined,
        empresaMotivoSaldo: form.empresaMotivoSaldo || undefined,
        empresaEstado: form.empresaEstado !== '' ? Number(form.empresaEstado) : undefined,
        empresaSaldoActual: form.empresaSaldoActual !== '' ? Number(form.empresaSaldoActual) : undefined,
      })
      setAbierto(false)
      setEditando(null)
      setExito(`Empresa ${editando.empresaCodigo} modificada correctamente`)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  // Eliminar (empresaww.cs:3472-3480 S222 'DO DELETE'): el backend replica
  // OnDeleteControls0F3 (empresa_bc.cs:835-880, BC000F11..F16): Usuario, Pago,
  // Auditoria, Supervisor y Socio → 409.
  const eliminar = (fila: any) => {
    if (!window.confirm(`¿Eliminar la empresa ${fila.empresaCodigo}?`)) return
    api.delete(`/superadmin/empresas/${fila.empresaCodigo}`)
      .then(() => {
        setAviso('Empresa eliminada.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  // 'Observa' y 'Limpiar' EXISTEN en el GX como columnas de acción del grid
  // (WWActionColumn, empresaww.cs:4273 EVSEOBSERVA y 4282 EVLIMPIAR con tooltip
  // "Limpiar Empresas Inactivas"), pero su lógica de evento NO es visible en el
  // código generado (edtavSeobserva_Jsonclick/edtavLimpiar_Jsonclick vacíos,
  // empresaww.cs:5174-5176) → sin evidencia no se replica (regla: no inventar).
  const observa = () =>
    setAviso(`Pendiente con evidencia: 'Observa' es WWActionColumn del grid empresaww (empresaww.cs:4273) — lógica de evento no visible en el .cs generado (Jsonclick vacío, empresaww.cs:5176)`)
  const limpiar = () =>
    setAviso(`Pendiente con evidencia: 'Limpiar' (Limpiar Empresas Inactivas) es WWActionColumn del grid empresaww (empresaww.cs:4282) — lógica de evento no visible en el .cs generado (Jsonclick vacío, empresaww.cs:5174)`)
  const rowActions: WwRowAction[] = [
    { label: 'Observa', onClick: observa },
    { label: 'Limpiar', onClick: limpiar },
    { label: 'Bloquear', onClick: (fila) => cambiarEstado(fila, 'deactivate', 'bloqueada') },
    { label: 'Desbloquear', onClick: (fila) => cambiarEstado(fila, 'activate', 'desbloqueada') },
  ]

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Empresa</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {exito && <CAlert color="success" dismissible onClose={() => setExito('')}>{exito}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <WwGrid
        title="Empresa"
        columns={columnas}
        // [RGM] 2026-09-29 (pedido Rafael: "quede solo esa empresa seleccionada
        // — me confunde no saber cuál edito"): mientras el modal de edición está
        // abierto, la grilla muestra SOLO la empresa en edición.
        rows={editando
          ? rows.filter((r) => String(r.empresaCodigo ?? '').trim() === String(editando.empresaCodigo ?? '').trim())
          : rows}
        onTotalChange={setTotalReg}
        loading={cargando}
        onEditar={abrirEditar}
        onEliminar={eliminar}
        rowActions={rowActions}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }} size="lg">
        <CModalHeader>
          <CModalTitle>
            {/* [RGM] 2026-09-29 (pedido Rafael): que se lea QUÉ empresa se está
                editando — código + nombre + representante. */}
            Editar empresa — {String(editando?.empresaCodigo ?? '').trim()}
            {String(editando?.empresaNombre ?? '').trim()
              ? ` · ${String(editando?.empresaNombre).trim()}` : ''}
            {String(editando?.empresaRepreLegal ?? '').trim()
              ? ` · ${String(editando?.empresaRepreLegal).trim()}` : ''}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            <CRow className="g-3">
              <div className="w-100"><small className="text-body-secondary fw-semibold">Empresa</small></div>
              {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
              <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={form.empresaNombre} onChange={(e) => setForm({ ...form, empresaNombre: e.target.value.toUpperCase() })} /></CCol>
              {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
              <CCol md={6}><CFormLabel>Repre Legal</CFormLabel><CFormInput value={form.empresaRepreLegal} onChange={(e) => setForm({ ...form, empresaRepreLegal: e.target.value.toUpperCase() })} /></CCol>
              <CCol md={6}><CFormLabel>Dirección</CFormLabel><CFormInput value={form.empresaDireccion} onChange={(e) => setForm({ ...form, empresaDireccion: e.target.value })} onBlur={(e) => setForm({ ...form, empresaDireccion: capitalizarPalabras(e.target.value) })} /></CCol>
              <CCol md={3}><CFormLabel>Teléfono</CFormLabel><CFormInput value={form.empresaTelefono} onChange={(e) => setForm({ ...form, empresaTelefono: e.target.value })} /></CCol>
              <CCol md={3}><CFormLabel>Celular</CFormLabel><CFormInput value={form.empresaCelular} onChange={(e) => setForm({ ...form, empresaCelular: e.target.value })} /></CCol>
              <CCol md={6}><CFormLabel>Email</CFormLabel><CFormInput value={form.empresaEmail} onChange={(e) => setForm({ ...form, empresaEmail: e.target.value })} /></CCol>
              <CCol md={3}><CFormLabel>País</CFormLabel><CFormInput value={form.empresaPais} onChange={(e) => setForm({ ...form, empresaPais: e.target.value })} onBlur={(e) => setForm({ ...form, empresaPais: capitalizarPalabras(e.target.value) })} /></CCol>
              <CCol md={3}><CFormLabel>Ciudad</CFormLabel><CFormInput value={form.empresaCiudad} onChange={(e) => setForm({ ...form, empresaCiudad: e.target.value })} onBlur={(e) => setForm({ ...form, empresaCiudad: capitalizarPalabras(e.target.value) })} /></CCol>
              <CCol md={3}><CFormLabel>Tipo Moneda</CFormLabel><CFormInput value={form.empresaTipoMoneda} onChange={(e) => setForm({ ...form, empresaTipoMoneda: e.target.value })} /></CCol>
              <CCol md={3}><CFormLabel>Estado</CFormLabel><CFormInput type="number" value={form.empresaEstado} onChange={(e) => setForm({ ...form, empresaEstado: e.target.value })} /></CCol>
              <CCol md={6}><CFormLabel>Motivo Saldo</CFormLabel><CFormInput value={form.empresaMotivoSaldo} onChange={(e) => setForm({ ...form, empresaMotivoSaldo: e.target.value })} onBlur={(e) => setForm({ ...form, empresaMotivoSaldo: capitalizarPalabras(e.target.value) })} /></CCol>
              <CCol md={6}><CFormLabel>Otros Saldos Pendientes</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                  <NumericFormat
                    className="form-control"
                    value={form.empresaSaldoActual}
                    onValueChange={(v) => setForm({ ...form, empresaSaldoActual: v.value })}
                    thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                    allowNegative={false} inputMode="decimal"
                  />
                </CInputGroup>
              </CCol>
            </CRow>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardando} onClick={() => setAbierto(false)}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? <CSpinner size="sm" /> : 'Guardar'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default EmpresasPage
